import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { PSApplication } from '../components/app/PSApplication'
import { Device } from '../models/Device'
import { RecordFactory, initializeCache, useSqlDb } from '../models/RecordFactory'
import { User } from '../models/User'
import { apiClient } from '../modules/apiclient'
import { useNotificationsManagerContext } from '../providers/NotificationsManagerProvider'
import {
    LOCAL_STORAGE_DEVICE_ID_KEY,
    LOCAL_STORAGE_LOGGED_IN_KEY,
    LOCAL_STORAGE_USER_ID_KEY,
} from '../utils/constants'
import { connectWebSocket } from '../utils/websocket'

export function useRegisterNewUserInit() {
    const [searchParams] = useSearchParams()
    const [user, setUser] = useState<User | undefined>(PSApplication.me())
    const [device, setDevice] = useState<Device | undefined>(PSApplication.device())
    const deviceID = searchParams.get('d')

    const dbReady = useSqlDb(true)

    // set user and device
    useEffect(() => {
        const f = async () => {
            const [userResp, deviceResp] = await Promise.all([
                apiClient.get('/user'),
                apiClient.get(`/device/${deviceID}`),
            ])
            const userJson = userResp.data
            const deviceJson = deviceResp.data.device

            localStorage.setItem(LOCAL_STORAGE_LOGGED_IN_KEY, '1')
            localStorage.setItem(LOCAL_STORAGE_USER_ID_KEY, userJson.id)
            localStorage.setItem(LOCAL_STORAGE_DEVICE_ID_KEY, deviceJson.device)

            const [storedUser, storedDevice] = await Promise.all([
                RecordFactory.getInstance().storeUser(userJson, true),
                RecordFactory.getInstance().storeDevice(deviceJson, true),
            ])

            await RecordFactory.getInstance().sync(storedUser.id!)

            await initializeCache()

            setUser(storedUser)
            setDevice(storedDevice)
        }
        if (!user && dbReady && deviceID) {
            f()
        }
    }, [user, dbReady, deviceID])

    const initComplete = useMemo(
        () => dbReady && Boolean(device) && Boolean(user),
        [dbReady, device, user]
    )

    return { initComplete }
}

export function useInit() {
    const user = PSApplication.me()
    const device = PSApplication.device()
    const [isWSConnected, setIsWSConnected] = useState(false)
    const { getNotificationData, postNotification } = useNotificationsManagerContext()

    const postNotificationRef = useRef(postNotification)
    postNotificationRef.current = postNotification
    const getNotificationDataRef = useRef(getNotificationData)
    getNotificationDataRef.current = getNotificationData

    // connect websocket
    useEffect(() => {
        if (!isWSConnected && !!user && !!device) {
            connectWebSocket({ postNotificationRef, getNotificationDataRef })
            setIsWSConnected(true)
        }
    }, [isWSConnected, user, device])

    useEffect(() => {
        const syncIfRequired = async () => {
            if (!user) return
            await RecordFactory.getInstance().sync(user.id!)
            await initializeCache()
        }

        // re-sync every 15 minutes
        const iv = setInterval(
            () => {
                syncIfRequired()
            },
            15 * 60 * 1000
        )

        return () => clearInterval(iv)
    }, [user])

    const initComplete = useMemo(
        () => isWSConnected && Boolean(user) && Boolean(device),
        [isWSConnected, user, device]
    )

    return { user, initComplete }
}
